import { FormEvent, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { formatDate, formatMoney } from "../lib/format";
import type {
  BoekhoudgemakAdministration,
  BoekhoudgemakPaymentFilter,
  BoekhoudgemakPreview,
  BoekhoudgemakStatus,
  ImportBatch,
  User,
} from "../types";

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoMonthsAgo(months: number): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - months, 1));
  return d.toISOString().slice(0, 10);
}

/** Pull bank transactions straight from Boekhoudgemak instead of a CSV. */
export function BoekhoudgemakPanel({ onImported }: { onImported: () => void }) {
  const [status, setStatus] = useState<BoekhoudgemakStatus | null>(null);
  const [admins, setAdmins] = useState<BoekhoudgemakAdministration[] | null>(null);
  const [chosenAdmin, setChosenAdmin] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [assignUserId, setAssignUserId] = useState("");
  const [start, setStart] = useState(isoMonthsAgo(1));
  const [end, setEnd] = useState(isoToday());
  const [includeIncoming, setIncludeIncoming] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState<BoekhoudgemakPaymentFilter>(
    "terminal",
  );
  const [skipPatterns, setSkipPatterns] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BoekhoudgemakPreview | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  async function loadStatus() {
    try {
      const st = await api.request<BoekhoudgemakStatus>("/boekhoudgemak/status");
      setStatus(st);
      setAssignUserId(st.default_user_id ? String(st.default_user_id) : "");
      if (st.connected && !st.administration) await loadAdministrations();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read connection status");
    }
  }

  async function loadUsers() {
    try {
      const list = await api.request<User[]>("/users");
      setUsers(list.filter((u) => u.is_active));
    } catch (e) {
      console.error(e);
    }
  }

  async function loadAdministrations() {
    try {
      setAdmins(
        await api.request<BoekhoudgemakAdministration[]>(
          "/boekhoudgemak/administrations",
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not list administrations");
    }
  }

  async function saveAdministration() {
    if (!chosenAdmin) return;
    setBusy(true);
    setError(null);
    try {
      const st = await api.request<BoekhoudgemakStatus>(
        "/boekhoudgemak/administration",
        { method: "POST", body: JSON.stringify({ administration_id: chosenAdmin }) },
      );
      setStatus(st);
      setAdmins(null);
      setMessage(`Using administration ${st.administration}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the administration");
    } finally {
      setBusy(false);
    }
  }

  async function saveDefaultUser() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.request<{ message: string }>("/boekhoudgemak/default-user", {
        method: "POST",
        body: JSON.stringify({
          user_id: assignUserId ? Number(assignUserId) : null,
          assign_existing: true,
        }),
      });
      setMessage(res.message);
      await loadStatus();
      onImported();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the default person");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    // The OAuth callback bounces back here with ?bhg=connected|error
    const params = new URLSearchParams(window.location.search);
    const result = params.get("bhg");
    if (result === "connected") {
      setMessage("Boekhoudgemak connected.");
    } else if (result === "error") {
      setError(`Connecting failed (${params.get("reason") || "unknown"}).`);
    }
    if (result) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    loadStatus();
    loadUsers();
  }, []);

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.request<BoekhoudgemakStatus>("/boekhoudgemak/connect", {
        method: "POST",
      });
      if (!res.authorize_url) throw new Error("No authorization URL returned");
      window.location.href = res.authorize_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start the connection");
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!confirm("Disconnect Boekhoudgemak? Imported transactions stay.")) return;
    setBusy(true);
    try {
      await api.request("/boekhoudgemak/disconnect", { method: "POST" });
      setMessage("Boekhoudgemak disconnected.");
      await loadStatus();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not disconnect");
    } finally {
      setBusy(false);
    }
  }

  function importPayload(externalIds?: string[]) {
    return {
      period_start: start,
      period_end: end,
      include_incoming: includeIncoming,
      payment_filter: paymentFilter,
      terminal_only: paymentFilter === "terminal",
      skip_patterns: skipPatterns.trim() || null,
      assign_user_id: assignUserId ? Number(assignUserId) : null,
      ...(externalIds ? { external_ids: externalIds } : {}),
    };
  }

  function clearPreview() {
    setPreview(null);
    setSelectedIds(new Set());
  }

  async function loadPreview(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const data = await api.request<BoekhoudgemakPreview>(
        "/boekhoudgemak/preview",
        {
          method: "POST",
          body: JSON.stringify(importPayload()),
        },
      );
      setPreview(data);
      setSelectedIds(
        new Set(
          data.items.filter((row) => !row.already_imported).map((row) => row.external_id),
        ),
      );
      if (data.items.length === 0) {
        setMessage(
          `No matching bank lines (${data.ignored} skipped by filters, ${data.fetched} fetched).`,
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed");
      clearPreview();
    } finally {
      setBusy(false);
    }
  }

  function toggleSelected(id: string, alreadyImported: boolean) {
    if (alreadyImported) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const selectedNewCount = useMemo(() => {
    if (!preview) return 0;
    return preview.items.filter(
      (row) => !row.already_imported && selectedIds.has(row.external_id),
    ).length;
  }, [preview, selectedIds]);

  async function confirmImport() {
    if (!preview || selectedNewCount === 0) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.request<{ message: string; batch: ImportBatch }>(
        "/boekhoudgemak/import",
        {
          method: "POST",
          body: JSON.stringify(importPayload([...selectedIds])),
        },
      );
      setMessage(res.message);
      clearPreview();
      await loadStatus();
      onImported();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  if (!status) return null;

  return (
    <section className="panel stack" style={{ marginTop: "1rem" }}>
      <h3 style={{ margin: 0, fontSize: "1.05rem" }}>Boekhoudgemak (bank)</h3>

      {!status.configured && (
        <p className="muted" style={{ margin: 0 }}>
          Not configured yet. Add <code>BHG_CLIENT_ID</code> and{" "}
          <code>BHG_CLIENT_SECRET</code> to the API environment, and register{" "}
          <code>/api/v1/boekhoudgemak/callback</code> as redirect URL in the Exact
          Connectivity Center.
        </p>
      )}

      {status.configured && !status.connected && (
        <>
          <p className="muted" style={{ margin: 0 }}>
            Sign in once with your Exact account to let this app read bank
            transactions. You pick the administration right after signing in.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={connect}
          >
            {busy ? "Opening Exact…" : "Connect Boekhoudgemak"}
          </button>
        </>
      )}

      {status.connected && admins !== null && (
        <div className="stack">
          <p className="muted" style={{ margin: 0 }}>
            Which administration should we read bank transactions from?
          </p>
          <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            <select
              value={chosenAdmin}
              onChange={(e) => setChosenAdmin(e.target.value)}
              style={{ flex: 1, minWidth: "14rem" }}
            >
              <option value="">— Choose —</option>
              {admins.map((a) => (
                <option key={a.administration_id} value={a.administration_id}>
                  {[a.code, a.description || a.short_name]
                    .filter(Boolean)
                    .join(" · ") || a.administration_id}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || !chosenAdmin}
              onClick={saveAdministration}
            >
              Use this one
            </button>
          </div>
          {admins.length === 0 && (
            <p className="muted" style={{ margin: 0 }}>
              Exact returned no administrations for this account.
            </p>
          )}
        </div>
      )}

      {status.connected && status.administration && admins === null && (
        <>
        <form className="stack" onSubmit={loadPreview}>
          <p className="muted" style={{ margin: 0 }}>
            Administration <strong>{status.administration}</strong>
            {status.last_sync_at &&
              ` · last sync ${new Date(status.last_sync_at).toLocaleString()}`}
            . Only outgoing payments are imported.
          </p>

          <div className="field">
            <label htmlFor="bhg-user">Assign all bank lines to</label>
            <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
              <select
                id="bhg-user"
                value={assignUserId}
                onChange={(e) => setAssignUserId(e.target.value)}
                style={{ flex: 1, minWidth: "14rem" }}
              >
                <option value="">— Unassigned —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={saveDefaultUser}
              >
                Save &amp; assign existing
              </button>
            </div>
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              Bank lines have no card number. Pick one person so they appear on
              their dashboard. “Save &amp; assign existing” also routes already
              imported unassigned lines to them.
            </span>
          </div>

          <div className="row" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
            <div className="field" style={{ flex: 1, minWidth: "10rem" }}>
              <label htmlFor="bhg-start">From</label>
              <input
                id="bhg-start"
                type="date"
                value={start}
                onChange={(e) => {
                  setStart(e.target.value);
                  clearPreview();
                }}
                required
              />
            </div>
            <div className="field" style={{ flex: 1, minWidth: "10rem" }}>
              <label htmlFor="bhg-end">To</label>
              <input
                id="bhg-end"
                type="date"
                value={end}
                onChange={(e) => {
                  setEnd(e.target.value);
                  clearPreview();
                }}
                required
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="bhg-skip">Ignore lines containing (comma separated)</label>
            <input
              id="bhg-skip"
              placeholder="mastercard, salaris, belastingdienst"
              value={skipPatterns}
              onChange={(e) => {
                setSkipPatterns(e.target.value);
                clearPreview();
              }}
            />
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              Use this to skip the collective credit-card direct debit — those
              purchases already come in through the ING CSV.
            </span>
          </div>
          <div className="field">
            <label htmlFor="bhg-filter">Which payments</label>
            <select
              id="bhg-filter"
              value={paymentFilter}
              onChange={(e) => {
                setPaymentFilter(e.target.value as BoekhoudgemakPaymentFilter);
                clearPreview();
              }}
            >
              <option value="terminal">Only pin/terminal</option>
              <option value="ideal">Only iDEAL</option>
              <option value="terminal_or_ideal">Pin/terminal + iDEAL</option>
              <option value="all">All outgoing bank lines</option>
            </select>
            <span className="muted" style={{ fontSize: "0.8rem" }}>
              iDEAL is for webshop invoices (e.g. FlinQ). Pin/terminal is POS.
              “All outgoing” also pulls rent, salaries, and supplier transfers.
            </span>
          </div>
          <label className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={includeIncoming}
              onChange={(e) => {
                setIncludeIncoming(e.target.checked);
                clearPreview();
              }}
            />
            <span>Also import incoming payments</span>
          </label>
          <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? "Fetching…" : preview ? "Refresh preview" : "Preview bank lines"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={loadAdministrations}
            >
              Change administration
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={disconnect}
            >
              Disconnect
            </button>
          </div>
        </form>
        {preview && (
          <div className="stack" style={{ marginTop: "1rem" }}>
            <div className="row between" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
              <p className="muted" style={{ margin: 0 }}>
                {preview.new_count} new
                {preview.already_imported_count
                  ? ` · ${preview.already_imported_count} already in the app`
                  : ""}
                {preview.ignored ? ` · ${preview.ignored} skipped by filters` : ""}
                {` · ${preview.fetched} fetched from Boekhoudgemak`}
              </p>
              <div className="row" style={{ gap: "0.35rem" }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={() =>
                    setSelectedIds(
                      new Set(
                        preview.items
                          .filter((row) => !row.already_imported)
                          .map((row) => row.external_id),
                      ),
                    )
                  }
                >
                  Select new
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={() => setSelectedIds(new Set())}
                >
                  Select none
                </button>
              </div>
            </div>
            {preview.items.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                Nothing to import with this filter. Try pin/terminal + iDEAL, or
                all outgoing.
              </p>
            ) : (
              <div className="bhg-preview-list">
                {preview.items.map((row) => {
                  const checked = selectedIds.has(row.external_id);
                  const kindLabel =
                    row.payment_kind === "ideal"
                      ? "iDEAL"
                      : row.payment_kind === "terminal"
                        ? "Pin"
                        : "Bank";
                  return (
                    <label
                      key={row.external_id}
                      className={`txn-card${checked ? " preview-selected" : ""}${
                        row.already_imported ? " preview-known" : ""
                      }`}
                      style={{ cursor: row.already_imported ? "default" : "pointer" }}
                    >
                      <div className="row between" style={{ gap: "0.75rem" }}>
                        <span className="row" style={{ gap: "0.55rem", alignItems: "flex-start" }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={row.already_imported || busy}
                            onChange={() =>
                              toggleSelected(row.external_id, row.already_imported)
                            }
                          />
                          <span>
                            <span className="merchant">{row.merchant_name}</span>
                            <span
                              className="muted"
                              style={{ display: "block", fontSize: "0.8rem", marginTop: "0.2rem" }}
                            >
                              {formatDate(row.transaction_date)} · {kindLabel}
                              {row.already_imported ? " · already imported" : ""}
                            </span>
                            {row.description && (
                              <span
                                className="muted"
                                style={{
                                  display: "block",
                                  fontSize: "0.75rem",
                                  marginTop: "0.2rem",
                                }}
                              >
                                {row.description}
                              </span>
                            )}
                          </span>
                        </span>
                        <span className="amount">
                          {formatMoney(row.amount, row.currency || "EUR")}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || selectedNewCount === 0}
                onClick={() => void confirmImport()}
              >
                {busy
                  ? "Please wait…"
                  : `Import ${selectedNewCount} selected`}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={clearPreview}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        </>
      )}

      {error && <div className="alert">{error}</div>}
      {message && <div className="alert alert-ok">{message}</div>}
    </section>
  );
}
